import { getMarketingBundle } from "@/lib/marketing-attribution";
import { parseError, parseErrorCode } from "@/lib/parse-error";

import { PatientAuthType } from "@/types/enums/patient-auth-type";
import { PatientSource } from "@/types/enums/patient-source";
import { PatientCreate } from "@/types/patient";
import {
  CandidatesResponse,
  PatientClaimAuthMode,
} from "@/types/patient-claim";
import { SelfSchedulePatient } from "@/types/self-schedule";

import { requestData } from "@/api/api-config";
import { PatientPortalProfileUpdate } from "@/api/patient/portal";
import { authService } from "@/services/auth-service";

export class PortalEmailInUseError extends Error {
  constructor(message = "This email already has a portal account.") {
    super(message);
    this.name = "PortalEmailInUseError";
  }
}

export class PortalPatientAlreadyClaimedError extends Error {
  constructor(message = "This email already has portal access.") {
    super(message);
    this.name = "PortalPatientAlreadyClaimedError";
  }
}

export class PortalAlreadyLinkedInTenantError extends Error {
  constructor(message = "This account is already linked at this clinic.") {
    super(message);
    this.name = "PortalAlreadyLinkedInTenantError";
  }
}

export class PortalLoginRequiredError extends Error {
  constructor(message = "Sign in with your portal email and password.") {
    super(message);
    this.name = "PortalLoginRequiredError";
  }
}

/** The SMS code was entered too long ago to open a portal account. */
export class PhoneCodeExpiredError extends Error {
  constructor(message = "Your verification code has expired.") {
    super(message);
    this.name = "PhoneCodeExpiredError";
  }
}

export interface CreatePortalAccountPayload {
  patientId?: string;
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  /** Stamped on a newly created patient only. */
  patientSource?: PatientSource;
  /** The location the visitor was booking at; a newly created patient only. */
  primaryLocationId?: string;
}

export type PatientAuthResponse = SelfSchedulePatient & {
  patientType: PatientAuthType;
};

export async function createPortalAccount(
  payload: CreatePortalAccountPayload,
): Promise<PatientAuthResponse> {
  try {
    const patient = await requestData<PatientAuthResponse>(
      "auth/patient/portal-account",
      "post",
      // Attach first-party attribution (C3) — idempotent server-side.
      { json: { ...payload, marketing: getMarketingBundle() } },
      false,
    );
    if (!patient) {
      throw new Error("Portal account creation returned no patient.");
    }
    return patient;
  } catch (err: any) {
    if (err?.response?.status === 409) {
      const code = await parseErrorCode(err);
      if (code === "email_already_in_use") {
        throw new PortalEmailInUseError(await parseError(err));
      }
    }
    throw err;
  }
}

export interface LinkExistingPortalAccountPayload {
  patientId?: string;
  phoneOtpToken: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  /** Stamped on a newly created patient only. */
  patientSource?: PatientSource;
  /** The location the visitor was booking at; a newly created patient only. */
  primaryLocationId?: string;
}

export async function linkExistingPortalAccount(
  payload: LinkExistingPortalAccountPayload,
): Promise<PatientAuthResponse> {
  try {
    const patient = await requestData<PatientAuthResponse>(
      "auth/patient/portal-account/link-existing",
      "post",
      { json: payload },
      false,
    );
    if (!patient) {
      throw new Error("Portal account link returned no patient.");
    }
    return patient;
  } catch (err: any) {
    if (err?.response?.status === 409) {
      const code = await parseErrorCode(err);
      if (code === "patient_already_claimed") {
        throw new PortalPatientAlreadyClaimedError(await parseError(err));
      }
      if (code === "already_linked_in_tenant") {
        throw new PortalAlreadyLinkedInTenantError(await parseError(err));
      }
    }
    throw err;
  }
}

interface PhonePortalSignInResponse {
  customToken: string;
  patient: SelfSchedulePatient;
}

/**
 * Sign a phone-verified patient into the portal account their Patient row is
 * bound to. Swaps the Firebase session from the shared phone-OTP user to that
 * portal user, so phone and email/password both reach the same account.
 */
export async function signInToPortalAccountWithPhone(
  patientId: string,
): Promise<SelfSchedulePatient> {
  let response: PhonePortalSignInResponse | null | undefined;
  try {
    response = await requestData<PhonePortalSignInResponse>(
      "auth/patient/portal-account/phone-sign-in",
      "post",
      { json: { patientId } },
      false,
    );
  } catch (err: any) {
    if (err?.response?.status === 409) {
      const code = await parseErrorCode(err);
      if (code === "portal_login_required") {
        throw new PortalLoginRequiredError(await parseError(err));
      }
    }
    if (
      err?.response?.status === 401 &&
      (await parseErrorCode(err)) === "phone_code_expired"
    ) {
      throw new PhoneCodeExpiredError(await parseError(err));
    }
    throw err;
  }
  if (!response) {
    throw new Error("Phone sign-in returned no portal account.");
  }
  await authService.signInWithCustomToken(response.customToken);
  return response.patient;
}

export async function listPatientCandidates(
  signal?: AbortSignal,
): Promise<CandidatesResponse | null> {
  const response = await requestData<CandidatesResponse>(
    `auth/patient/candidates`,
    "post",
    { signal },
    false,
  );
  if (!response) return null;
  if (response.authMode === PatientClaimAuthMode.PORTAL) {
    return {
      authMode: PatientClaimAuthMode.PORTAL,
      patient: response.patient,
    };
  }
  if (response.authMode === PatientClaimAuthMode.NONE) {
    return { authMode: PatientClaimAuthMode.NONE };
  }
  return {
    authMode: PatientClaimAuthMode.PHONE,
    candidates: response.candidates,
    claimedPatientId: response.claimedPatientId ?? null,
  };
}

export async function claimPatient(
  patientId: string,
): Promise<SelfSchedulePatient | null> {
  const claimed = await requestData<SelfSchedulePatient>(
    `auth/patient/claim`,
    "post",
    // Attach referral attribution so a claimed (pre-existing) patient who
    // followed a referral link is attributed just like a fresh create.
    { json: { patientId, marketing: getMarketingBundle() } },
    false,
  );
  await authService.refreshAuthToken();
  return claimed ?? null;
}

export async function createAndClaimPatient(
  patient: PatientCreate,
): Promise<PatientAuthResponse | null> {
  const created = await requestData<PatientAuthResponse>(
    `auth/patient`,
    "post",
    // Attach first-party attribution (C3) — idempotent server-side.
    { json: { ...patient, marketing: getMarketingBundle() } },
    false,
  );
  await authService.refreshAuthToken();
  return created ?? null;
}

export async function updatePatient(
  patientId: string,
  patientUpdate: PatientPortalProfileUpdate,
): Promise<SelfSchedulePatient | null> {
  const updated = await requestData<SelfSchedulePatient>(
    `auth/patient/${patientId}`,
    "put",
    { json: patientUpdate },
    true,
  );
  return updated ?? null;
}
